import ReactiveDialog from "../../common/ReactiveDialog";
import { Box, Typography, FormControl, MenuItem } from "@mui/material";
import TextField from "components/common/TextField";
import Select from "components/common/Select";
import { useFetchInvoiceHistory } from "../../../services/advisor";
import Loading from "../../common/Loading";
import { FetchInvoiceHistoryResponse_InvoiceMonth } from "protogen/advisors_service_pb";
import { useEffect, useState } from "react";
import { UpdateInvoiceRequest } from "protogen/billing_service_pb";
import { useUpdateInvoice } from "services/billing";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { DatePicker } from "@mui/x-date-pickers";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: () => void;
};

type ChangeCurrentInvoiceProps = {
  invoices: FetchInvoiceHistoryResponse_InvoiceMonth[];
  selectedInvoice: FetchInvoiceHistoryResponse_InvoiceMonth;
  selectedInvoiceRef: string;
  onInvoiceChange: (invoiceRef: string) => void;
  onHoursChange: (hours: number | null) => void;
  onStartDateChange: (date: string | null) => void;
  onEndDateChange: (date: string | null) => void;
  disabled: boolean;
  errors: { [key: string]: string };
};

const formatHours = (hours: number) =>
  Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(1);

const ChangeCurrentInvoiceContent = ({
  invoices,
  selectedInvoice,
  selectedInvoiceRef,
  onInvoiceChange,
  onHoursChange,
  onStartDateChange,
  onEndDateChange,
  disabled,
  errors,
}: ChangeCurrentInvoiceProps) => {
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Typography variant="body">
        Update the invoice for any billing period. Select a period below to
        modify it.
      </Typography>
      {invoices.length > 1 && (
        <FormControl fullWidth>
          <Select
            variant="outlined"
            value={selectedInvoiceRef}
            onChange={(e) => onInvoiceChange(e.target.value as string)}
            label="Billing period"
            disabled={disabled}
          >
            {invoices.map((invoice) => (
              <MenuItem key={invoice.ref} value={invoice.ref}>
                {invoice.startDate} - {invoice.endDate} (
                {formatHours(invoice.serviceHours)} hours)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Typography variant="body">
        The selected period runs from{" "}
        <strong>{selectedInvoice.startDate}</strong> to{" "}
        <strong>{selectedInvoice.endDate}</strong> for a total of{" "}
        <strong>{formatHours(selectedInvoice.serviceHours)}</strong> hours.
      </Typography>
      <TextField
        label="Hours"
        type="number"
        onChange={(e) =>
          onHoursChange(e.target.value ? Number(e.target.value) : null)
        }
        fullWidth
        inputProps={{ min: 0, step: 0.5 }}
      />
      <Box display="flex" flexDirection="column" gap="4px">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            flexDirection: "row",
          }}
        >
          <DatePicker
            sx={{
              width: "100%",
              ".MuiButtonBase-root": { marginRight: "unset" },
            }}
            label="Period start date"
            disabled={disabled}
            onChange={(d) => {
              onStartDateChange(
                d && !isNaN(d.getTime()) ? d.toLocaleDateString() : null,
              );
            }}
            format="MM/dd/yyyy"
            open={startDateOpen}
            onClose={() => setStartDateOpen(false)}
            slotProps={{
              textField: {
                error: !!errors.startDate,
                helperText: errors.startDate,
                onClick: () => !disabled && setStartDateOpen(true),
              },
            }}
          />
          <DatePicker
            sx={{
              width: "100%",
              ".MuiButtonBase-root": { marginRight: "unset" },
            }}
            label="Period end date"
            disabled={disabled}
            onChange={(d) => {
              onEndDateChange(
                d && !isNaN(d.getTime()) ? d.toLocaleDateString() : null,
              );
            }}
            format="MM/dd/yyyy"
            open={endDateOpen}
            onClose={() => setEndDateOpen(false)}
            slotProps={{
              textField: {
                error: !!errors.startDate,
                helperText: errors.startDate,
                onClick: () => !disabled && setEndDateOpen(true),
              },
            }}
          />
        </Box>
      </Box>
      <Typography variant="bodySmall">
        Each field can be modified independently, it's ok to leave fields empty.
      </Typography>
    </Box>
  );
};

export default ({ familyRef, open, setOpen, onUpdate }: Props) => {
  const [hours, setHours] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<
    FetchInvoiceHistoryResponse_InvoiceMonth[]
  >([]);
  const [selectedInvoiceRef, setSelectedInvoiceRef] = useState<string>("");
  const { request: fetchRequest, loading: fetchLoading } =
    useFetchInvoiceHistory();
  useEffect(() => {
    const load = async () => {
      const resp = await fetchRequest({
        familyRef,
      });
      // Compare these as date objects, newest first.
      const sortedInvoices =
        resp?.invoices
          ?.slice()
          .sort((a, b) => Number(b.startDateSec) - Number(a.startDateSec)) ||
        [];
      setInvoices(sortedInvoices);
      if (sortedInvoices.length) {
        setSelectedInvoiceRef(sortedInvoices[0].ref);
      }
    };
    if (open) {
      load();
    }
  }, [open, familyRef]);

  const { request: updateRequest, loading: updateLoading } = useUpdateInvoice();
  const { pushSnack } = useSnackpack();
  const selectedInvoice = invoices.find(
    (inv) => inv.ref === selectedInvoiceRef,
  );
  const disabled =
    fetchLoading || updateLoading || !(startDate || endDate || hours);

  const updateInvioce = async () => {
    if (disabled || !selectedInvoice) return;
    await updateRequest(
      new UpdateInvoiceRequest({
        invoiceRef: selectedInvoice.ref,
        serviceHours: hours || undefined,
        periodStartDate: startDate || undefined,
        periodEndDate: endDate || undefined,
      }),
    );
    pushSnack("Invoice updated");
    setOpen(false);
    onUpdate();
  };
  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Modify Billing Period"
      primaryActionName="Update"
      primaryActionEnabled={!disabled}
      primaryAction={updateInvioce}
      fullWidthSize="sm"
    >
      {fetchLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : selectedInvoice ? (
        <ChangeCurrentInvoiceContent
          invoices={invoices}
          selectedInvoice={selectedInvoice}
          selectedInvoiceRef={selectedInvoiceRef}
          onInvoiceChange={setSelectedInvoiceRef}
          onHoursChange={setHours}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          errors={{}}
          disabled={fetchLoading || updateLoading}
        />
      ) : null}
    </ReactiveDialog>
  );
};
